import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOrganizationContacts } from '@/hooks/useOrganizationContacts';
import { useCreateProjectScope } from '@/hooks/useProjectScopes';
import { useScopeTypes } from '@/hooks/useScopeTypes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScopeStatusDropdown, type ScopeStatus } from './ScopeStatusDropdown';
import { CreateScopeTypeModal } from './CreateScopeTypeModal';
import { SearchableOrganizationCombobox } from '@/components/organizations/SearchableOrganizationCombobox';
import { OrganizationFormModal } from '@/components/forms/OrganizationFormModal';
import { AddContactModal } from '@/components/contacts/AddContactModal';
import type { Organization, ScopeType } from '@/lib/schemas';

// Batch 7.3.4: Schema needs to support templates (no organization_id required)
const addScopeSchema = z.object({
  // For templates: organization_id can be empty string or undefined
  // For projects: must be a valid UUID
  // IMPORTANT: Must use null (not undefined) so field is included in payload when clearing
  organization_id: z.union([z.string().uuid(), z.literal(''), z.null()]).optional().transform(val => val || null),
  scope_type_id: z.string().uuid('Scope type is required'),
  // IMPORTANT: Must use null (not undefined) so field is included in payload when clearing
  contact_id: z.union([z.string().uuid(), z.literal(''), z.null()]).optional().transform(val => val || null),
  status: z.string().default('not_started'),
  status_notes: z.string().optional(),
});

type AddScopeFormData = z.infer<typeof addScopeSchema>;

interface AddScopeModalProps {
  open: boolean;
  projectId: string;
  isTemplate?: boolean; // Batch 7.3.4: Templates can't have organizations
  defaultScopeType?: 'client' | null; // R8: preselect first client-type scope on open
  onOpenChange: (open: boolean) => void;
  onScopeAdded: () => void;
}

export function AddScopeModal({
  open,
  projectId,
  isTemplate = false, // Batch 7.3.4: Default to false for backward compatibility
  defaultScopeType = null,
  onOpenChange,
  onScopeAdded,
}: AddScopeModalProps) {
  // Phase 7.5.4: Removed useOrganizations with limit 1000 band-aid
  // Organizations now fetched within SearchableOrganizationCombobox filtered by scope type
  const { data: scopeTypes, isLoading: isLoadingScopeTypes } = useScopeTypes();
  const createScopeMutation = useCreateProjectScope();

  // State for sub-modals
  const [showCreateScopeTypeModal, setShowCreateScopeTypeModal] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showCreateContactModal, setShowCreateContactModal] = useState(false);

  // Track the selected org name so AddContactModal can display it
  const [selectedOrgName, setSelectedOrgName] = useState('');

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue, watch } = useForm<AddScopeFormData>({
    resolver: zodResolver(addScopeSchema),
    defaultValues: {
      organization_id: '',
      scope_type_id: '',
      contact_id: null,
      status: 'not_started',
      status_notes: '',
    },
  });

  const selectedOrgId = watch('organization_id');
  const selectedScopeTypeId = watch('scope_type_id');
  const selectedContactId = watch('contact_id');
  const selectedStatus = watch('status');

  // Determine role filter based on selected scope type
  // Client scope types show clients, other scope types show consultants
  const selectedScopeType = scopeTypes?.find(st => st.id === selectedScopeTypeId);
  const organizationRole = selectedScopeType?.is_client_type ? 'client' : 'consultant';

  // Fetch contacts for selected organization
  // Phase 7.5.5: Added isLoading to show loading state in contact dropdown
  const { data: contactsData, isLoading: isLoadingContacts } = useOrganizationContacts(selectedOrgId, {
    enabled: !!selectedOrgId,
  });

  // Phase 7.5: Auto-fill scope type removed - now using scope-first pattern
  // Scope type is selected BEFORE organization, so no need to auto-fill

  // R8: When opened with defaultScopeType='client', preselect the first client-type scope
  useEffect(() => {
    if (open && defaultScopeType === 'client' && !selectedScopeTypeId && scopeTypes) {
      const firstClient = scopeTypes.find(st => st.is_client_type);
      if (firstClient) setValue('scope_type_id', firstClient.id);
    }
  }, [open, defaultScopeType, selectedScopeTypeId, scopeTypes, setValue]);

  // Handle scope type creation - auto-select the newly created type
  const handleScopeTypeCreated = (newScopeType: ScopeType) => {
    setValue('scope_type_id', newScopeType.id);
    setShowCreateScopeTypeModal(false);
  };

  // Handle scope type dropdown value change - check for special "_create" value
  const handleScopeTypeChange = (value: string) => {
    if (value === '_create') {
      setShowCreateScopeTypeModal(true);
    } else {
      setValue('scope_type_id', value);
    }
  };

  // When org is selected from the combobox, reset contact and capture name
  const handleOrgSelect = (id: string, name: string) => {
    setValue('contact_id', null);
    setSelectedOrgName(name);
  };

  // When a new org is created inline, auto-select it
  const handleOrgCreated = (org: Organization) => {
    setValue('organization_id', org.id);
    setValue('contact_id', null);
    setSelectedOrgName(org.name);
  };

  // Handle contact dropdown - intercept "_create" to open AddContactModal
  const handleContactChange = (value: string) => {
    if (value === '_create') {
      setShowCreateContactModal(true);
    } else {
      setValue('contact_id', value === 'none' ? null : value);
    }
  };

  // When a new contact is created inline, auto-select it
  const handleContactCreated = (contactId: string) => {
    setValue('contact_id', contactId);
  };

  const onSubmit = async (data: AddScopeFormData) => {
    try {
      // BUG FIX: Use mutateAsync pattern (same as CreateScopeTypeModal)
      // Await the mutation to ensure all cache updates and re-renders complete
      // before closing the modal. This prevents race conditions where the modal
      // tries to close while React Query is still re-rendering from invalidations.
      await createScopeMutation.mutateAsync({
        projectId: projectId,
        // Batch 7.3.4: For templates, organization_id can be undefined
        organizationId: data.organization_id || undefined,
        scopeTypeId: data.scope_type_id,
        contactId: data.contact_id || undefined,
        status: data.status,
        statusNotes: data.status_notes,
      });

      // At this point, the mutation hook's onSuccess has already:
      // - Invalidated queries
      // - Shown success toast
      // - All re-renders from query invalidation are complete

      // Now safely close modal after everything settled
      onScopeAdded();
      reset();
      setSelectedOrgName('');
      onOpenChange(false);
    } catch (error) {
      // Error toast already shown by mutation hook's onError
      console.error('Failed to add scope:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setSelectedOrgName('');
    onOpenChange(false);
  };

  // Phase 7.5.4: Only checking scope types loading (orgs load within combobox)
  const isLoading = isLoadingScopeTypes;

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogTitle className="sr-only">Add Scope</DialogTitle>
          <div className="text-center py-8">
            <p className="text-gray-500">Loading...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add Scope</DialogTitle>
            <DialogDescription>
              Add an organization to this project with a specific scope type.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Phase 7.5.4: Info text explaining scope-first pattern */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <p className="text-blue-900">
                <strong>Tip:</strong> Select the scope type first to see organizations that match that category.
              </p>
            </div>

            {/* Scope Type - NOW FIRST (Phase 7.5.4) */}
            <div>
              <Label htmlFor="scope_type">Scope Type *</Label>
              <Select
                value={selectedScopeTypeId}
                onValueChange={handleScopeTypeChange}
              >
                <SelectTrigger id="scope_type">
                  <SelectValue placeholder="Select scope type" />
                </SelectTrigger>
                <SelectContent>
                  {/* Phase 9.x: Removed is_client_type filter - multiple client scopes supported */}
                  {scopeTypes?.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.display_name}
                    </SelectItem>
                  ))}
                  <SelectSeparator />
                  <SelectItem value="_create" className="text-sky-600 font-medium">
                    + New Scope Type
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.scope_type_id && (
                <p className="text-sm text-red-600 mt-1">{errors.scope_type_id.message}</p>
              )}
            </div>

            {/* Organization - NOW SECOND with SearchableOrganizationCombobox (Phase 7.5.4) */}
            {/* Batch 7.3.4: Templates show "N/A - Template" instead of search */}
            <div>
              <Label htmlFor="organization">
                Client {!isTemplate && <span className="text-red-600">*</span>}
              </Label>
              {isTemplate ? (
                <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                  N/A - Template
                </div>
              ) : (
                <SearchableOrganizationCombobox
                  value={selectedOrgId}
                  onValueChange={(value) => setValue('organization_id', value)}
                  onSelect={handleOrgSelect}
                  onCreateNew={() => setShowCreateOrgModal(true)}
                  role={organizationRole}
                  categoryId={selectedScopeTypeId}
                  placeholder={!selectedScopeTypeId ? "Select billing track first" : "Search or select client"}
                  searchPlaceholder="Search clients..."
                  disabled={!selectedScopeTypeId}
                  showCategory={true}
                  useServerSearch={true}
                />
              )}
              {!isTemplate && errors.organization_id && (
                <p className="text-sm text-red-600 mt-1">{errors.organization_id.message}</p>
              )}
            </div>

            {/* Contact - Batch 7.3.4: Disabled for templates (no organization) */}
            {/* Phase 7.5.5: Added loading state for contact dropdown */}
            <div>
              <Label htmlFor="contact">Contact <span className="text-gray-500 text-sm">(Optional)</span></Label>
              <Select
                value={isLoadingContacts ? 'loading' : (selectedContactId || 'none')}
                onValueChange={handleContactChange}
                disabled={isTemplate || !selectedOrgId || isLoadingContacts}
              >
                <SelectTrigger id="contact">
                  <SelectValue placeholder={
                    isTemplate
                      ? "N/A - Template"
                      : !selectedOrgId
                        ? "Select client first"
                        : "No contact"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingContacts ? (
                    <SelectItem value="loading">Loading contacts...</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="none">No contact</SelectItem>
                      {contactsData?.contacts?.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name}{contact.is_primary_contact ? ' (Primary)' : ''}
                        </SelectItem>
                      ))}
                      <SelectSeparator />
                      <SelectItem value="_create" className="text-sky-600 font-medium">
                        + New Contact
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="status">Status</Label>
              <ScopeStatusDropdown
                id="status"
                value={selectedStatus as ScopeStatus}
                onChange={(value) => setValue('status', value)}
              />
            </div>

            {/* Status Notes */}
            <div>
              <Label htmlFor="notes">Notes <span className="text-gray-500 text-sm">(Optional)</span></Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about this scope..."
                {...register('status_notes')}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">Add Scope</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Scope Type Modal (Issue #9 - Inline scope type creation) */}
      <CreateScopeTypeModal
        open={showCreateScopeTypeModal}
        onOpenChange={setShowCreateScopeTypeModal}
        onCreated={handleScopeTypeCreated}
      />

      {/* Inline organization creation */}
      <OrganizationFormModal
        open={showCreateOrgModal}
        onOpenChange={setShowCreateOrgModal}
        fieldSet="minimal"
        onSave={handleOrgCreated}
      />

      {/* Inline contact creation — only mounted when an org is selected */}
      {selectedOrgId && (
        <AddContactModal
          open={showCreateContactModal}
          onOpenChange={setShowCreateContactModal}
          organizationId={selectedOrgId}
          organizationName={selectedOrgName || undefined}
          onContactAdded={() => {}}
          onContactCreated={(contactId) => handleContactCreated(contactId)}
        />
      )}
    </>
  );
}
